import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TerminalSquare,
  Settings,
  UserCog,
  Plug,
  KeyRound,
  Code2,
  ScrollText,
  Layers,
  ChevronDown,
  Lock,
  ShieldCheck,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import MeinSshModal from './MeinSshModal';
import { mayAccess } from '../routeAccess';
import LanguageSwitcher from './LanguageSwitcher';
import ThemaSchalter from './ThemaSchalter';
import ChangePasswordModal from './ChangePasswordModal';
import TwoFactorModal from './TwoFactorModal';
import RegistryCredentialModal from './RegistryCredentialModal';

/**
 * Eyebrow bar: the two things that are *not* the day-to-day job.
 *
 * The sidebar answers "what does this platform do" — customers, products,
 * instances, deployments. Administering the platform itself and managing your
 * own account are different kinds of work, and mixing all three into one list
 * meant the operational items competed with settings nobody touches weekly.
 */

interface AdminItem {
  to: string;
  labelKey: string;
  descriptionKey: string;
  icon: React.ComponentType<{ size?: string | number; className?: string }>;
}

const ADMIN_ITEMS: AdminItem[] = [
  {
    to: '/users',
    labelKey: 'eyebrow.users',
    descriptionKey: 'eyebrow.usersDescription',
    icon: UserCog,
  },
  {
    to: '/integrations',
    labelKey: 'eyebrow.integrations',
    descriptionKey: 'eyebrow.integrationsDescription',
    icon: Plug,
  },
  {
    to: '/api-keys',
    labelKey: 'eyebrow.apiKeys',
    descriptionKey: 'eyebrow.apiKeysDescription',
    icon: KeyRound,
  },
  {
    to: '/embed-keys',
    labelKey: 'eyebrow.embedKeys',
    descriptionKey: 'eyebrow.embedKeysDescription',
    icon: Code2,
  },
  {
    to: '/base-stacks',
    labelKey: 'eyebrow.baseStacks',
    descriptionKey: 'eyebrow.baseStacksDescription',
    icon: Layers,
  },
  {
    to: '/audit',
    labelKey: 'eyebrow.auditLog',
    descriptionKey: 'eyebrow.auditLogDescription',
    icon: ScrollText,
  },
];

/**
 * Closes the open dropdown on a click outside the bar, or on Escape.
 *
 * Deliberately ONE handler for the whole eyebrow, scoped to a container that
 * holds both menus. Two separate hooks — one per menu — looked equivalent and
 * broke every link in both: clicking an item in the admin menu was outside the
 * *user* menu's ref, so that hook fired `close()` on mousedown and unmounted the
 * NavLink before the click could land on it. Nothing happened, in both menus.
 */
function useDismiss(onDismiss: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onDismiss]);
  return ref;
}

function initials(name?: string, email?: string): string {
  const source = name?.trim() || email?.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : source.slice(0, 2)).toUpperCase();
}

export default function Eyebrow() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [openMenu, setOpenMenu] = useState<'admin' | 'user' | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [showRegistryCredential, setShowRegistryCredential] = useState(false);
  const [showSsh, setShowSsh] = useState(false);

  // useCallback so the effect inside useDismiss does not re-register its
  // listeners on every render.
  const close = useCallback(() => setOpenMenu(null), []);
  const barRef = useDismiss(close);

  // Route changes come from clicking an entry — the menu should not stay open
  // over the page it just navigated to.
  useEffect(close, [pathname]);

  const role = user?.role;
  // Roles come from ROUTE_ROLES, the same table the router guards against — so
  // the menu can never offer a page that would then refuse to open.
  const visibleAdminItems = ADMIN_ITEMS.filter((item) => mayAccess(item.to, role));
  const onAdminPage = visibleAdminItems.some((i) => pathname.startsWith(i.to));

  return (
    <div
      ref={barRef}
      className="flex items-center justify-end gap-1 border-b border-slate-200 bg-slate-50 px-4 py-1.5"
    >
      <ThemaSchalter />
      <LanguageSwitcher />
      {/* ── PC settings ─────────────────────────────────────────────────── */}
      {/* Hidden entirely for roles with nothing in it (SUPPORT, SALES, BILLING)
          — an empty menu is worse than no menu. */}
      {visibleAdminItems.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setOpenMenu(openMenu === 'admin' ? null : 'admin')}
            aria-haspopup="menu"
            aria-expanded={openMenu === 'admin'}
            className={clsx(
              'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
              onAdminPage || openMenu === 'admin'
                ? 'bg-slate-200 text-slate-900'
                : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900',
            )}
          >
            <Settings size={14} />
            {t('eyebrow.pcSettings')}
            <ChevronDown
              size={13}
              className={clsx('transition-transform', openMenu === 'admin' && 'rotate-180')}
            />
          </button>

          {openMenu === 'admin' && (
            <div
              role="menu"
              className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-lg"
            >
              <div className="border-b border-slate-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {t('eyebrow.platformAdministration')}
              </div>
              {visibleAdminItems.map(({ to, labelKey, descriptionKey, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  role="menuitem"
                  className={({ isActive }) =>
                    clsx(
                      'flex items-start gap-3 px-3 py-2.5 transition-colors',
                      isActive ? 'bg-brand-50' : 'hover:bg-slate-50',
                    )
                  }
                >
                  <Icon size={16} className="mt-0.5 shrink-0 text-slate-500" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-800">{t(labelKey)}</span>
                    <span className="block text-xs text-slate-500">{t(descriptionKey)}</span>
                  </span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Own account ─────────────────────────────────────────────────── */}
      <div className="relative">
        <button
          onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')}
          aria-haspopup="menu"
          aria-expanded={openMenu === 'user'}
          className={clsx(
            'flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 text-xs font-medium transition-colors',
            openMenu === 'user'
              ? 'bg-slate-200 text-slate-900'
              : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900',
          )}
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-[10px] font-semibold text-white">
            {initials(user?.name, user?.email)}
          </span>
          <span className="hidden max-w-[14rem] truncate sm:inline">{user?.email}</span>
          <ChevronDown
            size={13}
            className={clsx('transition-transform', openMenu === 'user' && 'rotate-180')}
          />
        </button>

        {openMenu === 'user' && (
          <div
            role="menu"
            className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-lg"
          >
            <div className="border-b border-slate-100 px-3 py-2.5">
              <div className="truncate text-sm font-medium text-slate-800">
                {user?.name || user?.email}
              </div>
              <div className="truncate text-xs text-slate-500">{user?.email}</div>
              {role && (
                <span className="mt-1.5 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                  {t(`roles.${role}`)}
                </span>
              )}
            </div>

            <button
              role="menuitem"
              onClick={() => {
                close();
                setShowChangePassword(true);
              }}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Lock size={15} className="shrink-0 text-slate-500" />
              {t('eyebrow.changePassword')}
            </button>
            <button
              role="menuitem"
              onClick={() => {
                close();
                setShowTwoFactor(true);
              }}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <ShieldCheck size={15} className="shrink-0 text-slate-500" />
              {t('eyebrow.twoFactor')}
            </button>
            <button
              role="menuitem"
              onClick={() => {
                close();
                setShowRegistryCredential(true);
              }}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <KeyRound size={15} className="shrink-0 text-slate-500" />
              {t('eyebrow.registryCredential')}
            </button>
            <button
              role="menuitem"
              onClick={() => {
                close();
                setShowSsh(true);
              }}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <TerminalSquare size={15} className="shrink-0 text-slate-500" />
              {t('eyebrow.sshKeys')}
            </button>

            <div className="border-t border-slate-100">
              <button
                role="menuitem"
                onClick={logout}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
              >
                <LogOut size={15} className="shrink-0" />
                {t('eyebrow.signOut')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showSsh && <MeinSshModal onClose={() => setShowSsh(false)} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showTwoFactor && <TwoFactorModal onClose={() => setShowTwoFactor(false)} />}
      {showRegistryCredential && (
        <RegistryCredentialModal onClose={() => setShowRegistryCredential(false)} />
      )}
    </div>
  );
}
